import React, { useState } from "react";
import { RotateCcw, EyeIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import DynamicTable from "../../components/Table/DataTable";
import Layout from "../../components/Layout";
import TablePaginationActions from "../../components/Table/tablePagination";

const RefundsPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const services = [
    {
      id: "SRV001",
      petName: "Bella",
      owner: "John Doe",
      serviceType: "Grooming",
      amount: 1200,
      status: "Completed",
      date: "2025-11-10",
      provider: "Happy Paws Groomers",
    },
    {
      id: "SRV002",
      petName: "Max",
      owner: "Jane Smith",
      serviceType: "Dog Walking",
      amount: 600,
      status: "Pending",
      date: "2025-11-11",
      provider: "PetStride Services",
    },
    {
      id: "SRV003",
      petName: "Charlie",
      owner: "Mike Johnson",
      serviceType: "Vet Checkup",
      amount: 800,
      status: "Completed",
      date: "2025-11-09",
      provider: "CityVet Clinic",
    },
    {
      id: "SRV004",
      petName: "Lucy",
      owner: "Priya Sharma",
      serviceType: "Vaccination",
      amount: 1500,
      status: "Completed",
      date: "2025-11-08",
      provider: "PawCare Veterinary Hospital",
    },
    {
      id: "SRV005",
      petName: "Rocky",
      owner: "Ravi Kumar",
      serviceType: "Pet Boarding",
      amount: 3500,
      status: "Ongoing",
      date: "2025-11-07",
      provider: "TailWag StayHome",
    },
    {
      id: "SRV006",
      petName: "Luna",
      owner: "Emily White",
      serviceType: "Spa & Massage",
      amount: 2200,
      status: "Completed",
      date: "2025-11-06",
      provider: "Purrfect Spa",
    },
    {
      id: "SRV007",
      petName: "Simba",
      owner: "Amit Patel",
      serviceType: "Training Session",
      amount: 1800,
      status: "Cancelled",
      date: "2025-11-05",
      provider: "GoodBoy Academy",
    },
    {
      id: "SRV008",
      petName: "Milo",
      owner: "Sofia Garcia",
      serviceType: "Health Checkup",
      amount: 950,
      status: "Completed",
      date: "2025-11-04",
      provider: "CityVet Clinic",
    },
    {
      id: "SRV009",
      petName: "Coco",
      owner: "Rahul Verma",
      serviceType: "Pet Sitting",
      amount: 2500,
      status: "Pending",
      date: "2025-11-03",
      provider: "PetBuddy Care",
    },
    {
      id: "SRV010",
      petName: "Daisy",
      owner: "Sara Khan",
      serviceType: "Grooming + Bath",
      amount: 1700,
      status: "Completed",
      date: "2025-11-02",
      provider: "Happy Paws Groomers",
    },
  ];

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);

  const columns = [
    { key: "id", label: "Service ID", sortable: true },
    { key: "petName", label: "Pet Name" },
    { key: "owner", label: "Owner" },
    { key: "serviceType", label: "Service Type" },
    {
      key: "amount",
      label: "Amount (₹)",
      sortable: true,
      render: (row: any) => (
        <span className="font-semibold text-green-600">
          {formatCurrency(row.amount)}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row: any) => {
        const colors: any = {
          Pending: "#f59e0b",
          Completed: "#10b981",
          Cancelled: "#ef4444",
          Ongoing: "#3b82f6",
        };
        return (
          <span
            style={{
              backgroundColor: `${colors[row.status]}20`,
              color: colors[row.status],
              padding: "4px 8px",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          >
            {row.status}
          </span>
        );
      },
    },
    { key: "provider", label: "Service Provider" },
    { key: "date", label: "Date", sortable: true },
  ];

  const actions = [
    {
      label: "View Details",
      icon: <EyeIcon size={16} />,
      color: "#3b82f6",
      onClick: (row: any) => {
        navigate(`/refunds/${row.id}`, { state: row });
      },
    },
  ];

  return (
    <Layout>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold flex items-center gap-2 text-gray-800 dark:text-gray-100">
            <RotateCcw className="text-blue-500" size={24} />
            Refunds
          </h1>
        </div>

        <DynamicTable
          columns={columns}
          data={services.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
          )}
          loading={false}
          error={null}
          actions={actions}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={services.length}
          onPageChange={(newPage) => setPage(newPage)}
          onRowsPerPageChange={(newRows) => {
            setRowsPerPage(newRows);
            setPage(0);
          }}
          emptyMessage="No pet services found."
          ActionsComponent={TablePaginationActions}
        />
      </div>
    </Layout>
  );
};

export default RefundsPage;
